import { type Rect } from "@common/declarations";

export { type Rect };

export enum ContentAlign {
  Stretch = "stretch",
  StretchFixed = "stretchFixed",
  Left = "left",
  BottomCenter = "bottomCenter",
  BottomRight = "bottomRight",
  Right = "right",
  ToggleWidth = "toggleWidth"
}

export type AlignContent = (toggleRect: Rect, contentRect: Rect) => ContentPosition;

/**
 * Four letters: {L|R|C}{T|B|C}{L|R|C|S}{T|B|C|S}, describe how the toggle and the content should be position against each other
 * First two letters - the toggle point
 * Second two letters - the content point or S (stretch)
 *
 * Examples,
 *
 *  1. LBST - stretch (S) content below (T - top) (LB left-bottom point) the toggle
 *  2. LTSB - stretch (S) content above (T - top) (LT left-top point) the toggle
 *  3. RBRT - used for color/date-picker. Put right-top rect's point of the content to the right-bottom rect's point of the toggle.
 */

export interface ContentPosition {
  align: string;
  left: number;
  top: number;
  height?: number;
  width?: number;
  minWidth?: number;
}

const getLeft = (letter: "L" | "C" | "R", r: Rect): number => {
  switch (letter) {
    case "C": {
      return r.left + r.width / 2;
    }
    case "L": {
      return r.left;
    }
    case "R": {
      return r.left + r.width;
    }
    default:
      throw new Error(`Unsupported align letter: ${letter}`);
  }
};

const getTop = (letter: "T" | "C" | "B", r: Rect): number => {
  switch (letter) {
    case "C": {
      return r.top + r.height / 2;
    }
    case "T": {
      return r.top;
    }
    case "B": {
      return r.bottom;
    }
    default:
      throw new Error(`Unsupported align letter: ${letter}`);
  }
};

const getLeftOffset = (letter: "L" | "C" | "R" | "S", r: Rect): number | null => {
  switch (letter) {
    case "C": {
      return r.width / 2;
    }
    case "L": {
      return 0;
    }
    case "R": {
      return r.width;
    }
    case "S": {
      return null;
    }
    default:
      throw new Error(`Unsupported align letter: ${letter}`);
  }
};

const OFFSET = 4;
const getTopOffset = (letter: "T" | "C" | "B" | "S", r: Rect): number | null => {
  switch (letter) {
    case "C": {
      return r.height / 2;
    }
    case "T": {
      return -OFFSET;
    }
    case "B": {
      return r.height + OFFSET;
    }
    case "S": {
      return null;
    }
    default:
      throw new Error(`Unsupported align letter: ${letter}`);
  }
};

const getPosition = (tr: Rect, cr: Rect, align: string): ContentPosition => {
  if (align.length !== 4) throw new Error(`Unsupported align value: ${align}`);

  const left = getLeft(align[0] as any, tr);
  const top = getTop(align[1] as any, tr) + (window.visualViewport?.offsetTop ?? 0); // visualViewport.offsetTop - considers e.g. table/mobile keyboard
  const leftOffset = getLeftOffset(align[2] as any, cr);
  const topOffset = getTopOffset(align[3] as any, cr);

  return {
    align,
    left: Math.max(0, left - (leftOffset || 0)),
    top: top - (topOffset || 0),
    height: topOffset === null ? tr.height : undefined,
    width: leftOffset === null ? tr.width : undefined,
    minWidth: leftOffset !== null && leftOffset === 0 ? tr.width : undefined
  };
};

const convertAlign = (align: string | ContentAlign): string => {
  switch (align) {
    case ContentAlign.BottomCenter:
      return "CBCT";
    case ContentAlign.BottomRight:
      return "RBRT";
    case ContentAlign.Left:
      return "LBLT";
    case ContentAlign.Stretch:
      return "LBLT";
    case ContentAlign.StretchFixed:
      return "LBLT";
    case ContentAlign.Right:
      return "RBRT";
    case ContentAlign.ToggleWidth:
      return "LBST";
    default:
      return align;
  }
};

export const rectToPosition = (tr: Rect, cr: Rect, align: string | ContentAlign | undefined): ContentPosition => {
  const letterAlign = convertAlign(align ?? ContentAlign.Stretch);

  const cp = getPosition(tr, cr, letterAlign);
  switch (align) {
    case ContentAlign.StretchFixed: {
      cp.width = cr.width;
      break;
    }
  }

  let recommendedAlign = letterAlign;
  let windowHeight = window.visualViewport?.height ?? window.innerHeight;
  // adjust position trying to not go off the screen
  if (cp.top < 0 || cp.top + cr.height > windowHeight) {
    // check where there's more space at the top or at the bottom
    const topSpace = tr.top;
    const bottomSpace = windowHeight - tr.bottom;
    recommendedAlign = [
      letterAlign[0],
      letterAlign[1] !== "C" ? (topSpace > bottomSpace ? "T" : "B") : letterAlign[1],
      letterAlign[2],
      topSpace > bottomSpace ? "B" : "T"
    ].join("");
  }

  if (cp.left + cr.width > window.innerWidth) {
    recommendedAlign = ["R", letterAlign[1], "R", letterAlign[3]].join("");
  }

  return recommendedAlign === letterAlign ? cp : getPosition(tr, cr, recommendedAlign);
};
